import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAdminPaymentStore, useAuthStore } from '../../stores/root_store';

export const PaymentHistoryPage = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authStore = useAuthStore();
  const paymentStore = useAdminPaymentStore();

  // Get user param for sudo access
  const userUuid = searchParams.get('user');

  // Check if user is an assistant
  const isAssistant = authStore.user?.is_beta;

  // Redirect non-assistants (unless viewing via sudo with user param)
  useEffect(() => {
    if (authStore.initialized && !isAssistant && !userUuid) {
      navigate('/request');
    }
  }, [authStore.initialized, isAssistant, userUuid, navigate]);

  // Fetch payments on mount
  // If userUuid is provided, backend will check for sudo access
  useEffect(() => {
    if (authStore.initialized) {
      paymentStore.fetchPayments(true, userUuid || undefined);
    }
  }, [authStore.initialized, userUuid, paymentStore]);

  // Clean up when leaving
  useEffect(() => {
    return () => {
      paymentStore.clear();
    };
  }, [paymentStore]);

  const handleLoadMore = () => {
    paymentStore.fetchPayments(false, userUuid || undefined);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!authStore.initialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Payment History
      </Typography>

      {userUuid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Viewing payments for user: {userUuid}
        </Alert>
      )}

      {paymentStore.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentStore.error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {paymentStore.paymentsLoading && paymentStore.allPayments.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : paymentStore.allPayments.length === 0 ? (
            <Typography color="text.secondary" align="center" py={4}>
              No payment history available
            </Typography>
          ) : (
            <List disablePadding>
              {paymentStore.allPayments.map((payment, index) => {
                const isExpanded = paymentStore.expandedPayments.has(payment.key);
                const requestData = paymentStore.paymentRequests.get(payment.key);
                const isLoadingRequests = paymentStore.paymentRequestsLoading.get(payment.key) ?? false;

                return (
                  <Box key={payment.key}>
                    {index > 0 && <Divider />}
                    <ListItem
                      component="div"
                      onClick={() => paymentStore.togglePaymentExpanded(payment.key)}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1,
                        py: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          paymentStore.togglePaymentExpanded(payment.key);
                        }}
                      >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                      <ListItemText
                        primary={payment.name || 'Payment'}
                        secondary={
                          <>
                            {formatDate(payment.date_created)}
                            {' | '}
                            {payment.request_count} task{payment.request_count !== 1 ? 's' : ''}
                          </>
                        }
                        sx={{ flex: 1 }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          minWidth: { sm: 200 },
                          justifyContent: { xs: 'space-between', sm: 'flex-end' },
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body1" fontWeight="bold">
                            {formatCurrency(payment.total)}
                          </Typography>
                          {payment.bonus > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              ({formatCurrency(payment.base_pay)} + {formatCurrency(payment.bonus)} bonus)
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={payment.settled ? 'Paid' : payment.status || 'Pending'}
                          color={payment.settled ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                    </ListItem>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 3, pb: 2 }}>
                        {isLoadingRequests ? (
                          <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : requestData ? (
                          <>
                            {requestData.tasks.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Tasks ({requestData.tasks.length})
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Title</TableCell>
                                      <TableCell align="right">Paid</TableCell>
                                      <TableCell align="right">Date Solved</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {requestData.tasks.map((req) => (
                                      <TableRow key={req.key}>
                                        <TableCell>
                                          <Link
                                            component={RouterLink}
                                            to={`/assistant/request/${req.uuid || req.key}`}
                                          >
                                            {req.title || '(untitled)'}
                                          </Link>
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(req.solver_paid_amount ?? 0)}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatDate(req.date_solved)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                            {requestData.mentor_reviews.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Mentor Reviews ({requestData.mentor_reviews.length})
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Title</TableCell>
                                      <TableCell align="right">Mentor Pay</TableCell>
                                      <TableCell align="right">Date Solved</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {requestData.mentor_reviews.map((req) => (
                                      <TableRow key={req.key}>
                                        <TableCell>
                                          <Link
                                            component={RouterLink}
                                            to={`/assistant/request/${req.uuid || req.key}`}
                                          >
                                            {req.title || '(untitled)'}
                                          </Link>
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(req.mentor_price ?? 0)}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatDate(req.date_solved)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                            {requestData.tasks.length === 0 && requestData.mentor_reviews.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                No request details available for this payment.
                              </Typography>
                            )}
                          </>
                        ) : null}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          )}

          {paymentStore.paymentsHasMore && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                onClick={handleLoadMore}
                disabled={paymentStore.paymentsLoading}
                variant="outlined"
              >
                {paymentStore.paymentsLoading ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : null}
                Load More
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
});
